import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import get from 'lodash.get';
import { AbsoluteBox, Box, InputField, Typography, Grid } from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { Keyboard } from 'components/Keyboard';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { NumPadAction } from 'components/NumPad';
import { useActions } from 'hooks/useActions';
import { selectors as configSelectors, Configuration, ResultCriteria } from 'services/configuration';
import { selectors as customerSelectors, actions as customerActions } from 'services/customer';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { useStyles } from './styled';

const LocationLookupView: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const { getLocations, setIsLocal, resetError } = useActions({
    getLocations: customerActions.getLocations,
    setIsLocal: customerActions.setIsLocal,
    resetError: customerActions.resetError
  });

  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const loading = useSelector(customerSelectors.selectLoading);
  const locations = useSelector(customerSelectors.selectLocations);
  const customerError = useSelector(customerSelectors.selectError);
  const navigationPath = useSelector(ticketingSelectors.selectNavigationPath);

  const fontFamily = get(configuration, 'configuration.globalSettings.fonts.primary');
  const keyboard = get(configuration, 'ticketing.locationValidation.dataCollection.keyboard', 'alphaNumeric');
  const resultCriteria: ResultCriteria = get(configuration, 'ticketing.locationValidation.resultCriteria', {});
  const dataCollectionMessages = get(configuration, 'ticketing.locationValidation.dataCollection.messages', {});
  const lookupViewHeader = get(dataCollectionMessages, `header.${i18n.language}`, t('locationDataCollection.header'));
  const lookupViewSubheader = get(dataCollectionMessages, `subheader.${i18n.language}`, '');
  const errorOverlayMessages = get(configuration, 'ticketing.locationValidation.errorOverlayMessages', {});
  const [errorOverlayHeader, errorOverlaySubheader, errorRetryButton, errorSkipButton] = [
    get(errorOverlayMessages, `header.${i18n.language}`, t('locationDataCollection.errorOverlayHeader')),
    get(errorOverlayMessages, `subheader.${i18n.language}`, t('locationDataCollection.errorOverlaySubheader')),
    get(errorOverlayMessages, `buttons.${i18n.language}[0]`, t('commonText.tryAgain')),
    get(errorOverlayMessages, `buttons.${i18n.language}[1]`, t('commonText.skip'))
  ];

  const [postalCodeInput, setPostalCodeInput] = useState('');
  const [uiLoading, setUILoading] = useState(false);
  const [validationError, setValidationError] = useState(false);

  const classes = useStyles({ fontFamily })();

  const path = navigationPath === 'donations' ? '/ticketing/donation' : '/ticketing/genres';

  useEffect(() => {
    if (customerError) setUILoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerError]);

  // Get the configured resultCriteria and compare to find matches
  useEffect(() => {
    if (locations) {
      const { cities = [], counties = [], states = [], countries = [] } = resultCriteria;

      const isValidLocation = locations.some((location) => {
        const matchesCity = !cities.length || cities.includes(location.city);
        const matchesCounty = !counties.length || counties.includes(location.county);
        const matchesState = !states.length || states.includes(location.state);
        const matchesCountry = !countries.length || countries.includes(location.country);

        return matchesCity && matchesCounty && matchesState && matchesCountry;
      });

      if (!isValidLocation) {
        setValidationError(true);
        setUILoading(false);
      } else {
        setIsLocal(true);
        navigate(path);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, configuration]);

  const handleKeyboardClick = (_: React.MouseEvent<HTMLButtonElement>, v: string) => {
    if (v === 'delete') {
      setPostalCodeInput((prevValue) => prevValue.slice(0, -1));
    } else if (v === 'clear') {
      setPostalCodeInput('');
    } else {
      setPostalCodeInput((prevValue) => prevValue + v);
    }
  };

  // Navigate back
  const onPrevPageClick = () => {
    navigate('/ticketing/start-purchase');
  };

  // Navigate forward and call the getLocations API
  const onNextPageClick = () => {
    setUILoading(true);
    getLocations({ postalCode: postalCodeInput });
  };

  // When skip is clicked on error overlay
  const onSkipPageClick = () => {
    resetError();
    navigate(path);
  };

  // Resetting error when try again is clicked
  const onTryAgainClick = () => {
    setValidationError(false);
    resetError();
  };

  const isValidPostalCode =
    postalCodeInput &&
    postalCodeInput.length &&
    !postalCodeInput.includes('.com') &&
    /^[a-zA-Z0-9\s-]+$/.test(postalCodeInput);

  if (loading || uiLoading) return <LoadingOverlay style={{ zIndex: '99' }} />;

  if (customerError || validationError) {
    return (
      <ActionOverlay
        height={600}
        title={<Typography className={classes.errorOverlayHeader}>{errorOverlayHeader}</Typography>}
        description={errorOverlaySubheader}
        confirmText={errorSkipButton}
        onConfirm={onSkipPageClick}
        cancelText={errorRetryButton}
        onCancel={onTryAgainClick}
      />
    );
  }

  return (
    <>
      <Grid className={classes.lookupViewWrapper}>
        <Box flexOverride="none" margin="6">
          <Typography fontSize={72} fontWeight={800} align="center">
            {lookupViewHeader}
          </Typography>
          <Typography fontSize={30} align="center">
            {lookupViewSubheader}
          </Typography>
        </Box>
        <Grid className={classes.inputContainer}>
          <InputField
            id="postal-code-input"
            data-testid="postal-code-input"
            autoFocus
            size="large"
            align="center"
            fullWidth
            value={postalCodeInput}
          />
        </Grid>
      </Grid>
      <AbsoluteBox raised fullWidth bg="x-light-gray" bottom="0" left="0">
        <Grid>
          {keyboard === 'numpad' && (
            <NumPadAction
              onClick={handleKeyboardClick}
              isConfirmDisabled={!isValidPostalCode ? true : false}
              onConfirm={onNextPageClick}
              onBack={onPrevPageClick}
              layout="row"
              confirmConfig={{
                label: t('commonText.next'),
                backLabel: t('commonText.back'),
                customSize: 'x-large',
                width: 475,
                appearance: 'primary'
              }}
            />
          )}
          {keyboard === 'alphaNumeric' && (
            <Keyboard
              language={i18n.language}
              onClick={handleKeyboardClick}
              isNextDisabled={!isValidPostalCode ? true : false}
              onNext={onNextPageClick}
              onPrev={onPrevPageClick}
              nextText={t('commonText.next')}
              prevText={t('commonText.back')}
              data-testid="keyboard"
            />
          )}
        </Grid>
      </AbsoluteBox>
    </>
  );
};

export default LocationLookupView;
